import React from 'react';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm space-y-6">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-sm text-gray-600">Last updated: January 2026</p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <p>
              This Privacy Policy explains how we collect, use, store, and protect your information when you use
              TicketDisputer (the “Service”).
            </p>
            <p>By using the Service, you agree to the practices described in this policy.</p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">1. Information We Collect</h2>
            <h3 className="text-base font-semibold text-gray-900">1.1 Information You Provide Directly</h3>
            <p>When you use TicketDisputer, you may voluntarily provide:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Ticket information
                <ul className="list-disc pl-6 space-y-1">
                  <li>Ticket number</li>
                  <li>License plate number</li>
                  <li>Violation date, time, and location</li>
                </ul>
              </li>
              <li>
                Appeal responses
                <ul className="list-disc pl-6 space-y-1">
                  <li>Written explanations</li>
                  <li>Answers to guided questions</li>
                  <li>Additional details you choose to add</li>
                </ul>
              </li>
              <li>
                Evidence (optional)
                <ul className="list-disc pl-6 space-y-1">
                  <li>Photos, screenshots, or documents (e.g., receipts, permits)</li>
                </ul>
              </li>
              <li>
                Account or session identifiers
                <ul className="list-disc pl-6 space-y-1">
                  <li>Anonymous session IDs or authenticated user IDs (if enabled)</li>
                </ul>
              </li>
            </ul>
            <p>You are responsible for ensuring the accuracy of the information you submit.</p>

            <h3 className="text-base font-semibold text-gray-900">1.2 Information Collected Automatically</h3>
            <p>We may automatically collect limited technical information, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Browser type and version</li>
              <li>Device type and operating system</li>
              <li>Pages viewed and interaction timestamps</li>
              <li>Error logs and performance diagnostics</li>
            </ul>
            <p>We also use limited browser-side storage and service-generated identifiers to keep the Service working.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Authentication state and security-related session data</li>
              <li>Language preference and basic product settings</li>
              <li>Temporary workflow state, such as acceptance of review disclaimers</li>
            </ul>
            <p>This data is used only to operate, secure, and improve the Service.</p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Generate and structure parking ticket appeal content</li>
              <li>Provide AI-assisted suggestions and reviews</li>
              <li>Guide you through the appeal process step by step</li>
              <li>Improve system accuracy, usability, and reliability</li>
              <li>Detect and prevent abuse or misuse of the Service</li>
            </ul>
            <p>We do not use your data for advertising or marketing.</p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">3. AI Processing and Human Review</h2>
            <h3 className="text-base font-semibold text-gray-900">3.1 AI Usage</h3>
            <p>TicketDisputer uses automated systems (including AI models) to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Analyze the relevance and completeness of your responses</li>
              <li>Suggest improvements to appeal narratives</li>
              <li>Recommend next steps in the appeal flow</li>
            </ul>
            <p>AI suggestions are advisory only. You remain fully responsible for the final content of your appeal.</p>

            <h3 className="text-base font-semibold text-gray-900">3.2 No Legal Advice</h3>
            <p>TicketDisputer does not provide legal advice.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>AI suggestions are informational and procedural</li>
              <li>They do not guarantee appeal success</li>
              <li>They do not replace professional legal counsel</li>
            </ul>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">4. Data Sharing</h2>
            <p>We do not sell or rent your personal data.</p>
            <p>We may share limited information only when necessary:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Service providers (e.g., cloud hosting, storage, authentication, payments, email, AI infrastructure)
                <ul className="list-disc pl-6 space-y-1">
                  <li>These providers are bound by confidentiality and security obligations.</li>
                </ul>
              </li>
              <li>Examples of providers we may use include Firebase/Google Cloud, Stripe, Resend, and OpenAI.</li>
              <li>Legal requirements</li>
              <li>If required by law, court order, or lawful government request.</li>
            </ul>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">5. Cookies, Local Storage, and Similar Technologies</h2>
            <p>
              TicketDisputer uses limited cookies, local storage, and similar browser technologies that are primarily
              necessary to operate the Service securely and reliably.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Authentication and account session persistence</li>
              <li>Security protections, including abuse prevention and app integrity checks</li>
              <li>Language preference and user-interface continuity</li>
              <li>Temporary workflow state, such as whether a required review disclaimer was acknowledged</li>
            </ul>
            <p>
              We do not currently use the Service to run advertising cookies, third-party ad pixels, or cross-site
              behavioral advertising trackers.
            </p>
            <p>
              If we introduce non-essential analytics, advertising, or similar tracking technologies in the future, we
              may update this Privacy Policy and, where required, request additional consent before enabling them.
            </p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">6. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Appeal session data is retained only as long as necessary to provide the Service.</li>
              <li>Uploaded evidence may be deleted automatically after a defined retention period.</li>
              <li>You may request deletion of your data where applicable.</li>
            </ul>
            <p>Retention periods may vary depending on system requirements and legal obligations.</p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">7. Data Security</h2>
            <p>We take reasonable technical and organizational measures to protect your data, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Encrypted data transmission (HTTPS)</li>
              <li>Access controls and logging</li>
              <li>Restricted database and storage access controls</li>
            </ul>
            <p>However, no system is 100% secure. You use the Service at your own risk.</p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">8. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access the information we hold about you</li>
              <li>Request correction or deletion of your data</li>
              <li>Object to or restrict certain processing activities</li>
            </ul>
            <p>To exercise these rights, contact us using the information below.</p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">9. Children’s Privacy</h2>
            <p>TicketDisputer is not intended for use by individuals under 18 years of age.</p>
            <p>We do not knowingly collect personal data from children.</p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Updates will be posted on this page</li>
              <li>Continued use of the Service after changes constitutes acceptance</li>
            </ul>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <h2 className="text-lg font-semibold text-gray-900">11. Contact</h2>
            <p>If you have questions or concerns about this Privacy Policy, please contact:</p>
            <p>Email: support@ticketdisputer.app</p>
            <p>Service: TicketDisputer</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
